import {
  Col,
  CustomFieldInput,
  Row,
  SubSection,
} from "@administrate/piston-ux";
import { CustomFieldType } from "@administrate/piston-ux/lib/CustomFieldInput";
import { Maybe } from "@administrate/piston-ux/lib/types";
import { isObservableArray } from "mobx";
import { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { PointOfSaleLearnerFieldDefinition as LMSPointOfSaleLearnerFieldDefinition } from "../generated/lmsTypes";
import { PointOfSaleLearnerFieldDefinition as WebLinkPointOfSaleLearnerFieldDefinition } from "../generated/weblinkTypes";
import { Errors } from "../types/Portal";
import { getUniqueId } from "../utils/displayHelpers";
import CountryProvinceSelector from "./CountryProvinceSelector";

const browserLocaleTypes = ["date", "datetime", "time"];

const WrappedSubSection: FunctionComponent<{
  skipSubSection: boolean;
  title: string;
}> = ({ skipSubSection, title, children }) =>
  skipSubSection ? (
    <>{children}</>
  ) : (
    <SubSection titleLevel="span" title={title}>
      {children}
    </SubSection>
  );

type PosFieldDef =
  | WebLinkPointOfSaleLearnerFieldDefinition
  | LMSPointOfSaleLearnerFieldDefinition;
type PosFieldDefs = PosFieldDef[];

export const PointOfSaleLearnerFields: FunctionComponent<{
  posFields?: Maybe<PosFieldDefs>;
  learnerNumber: number;
  contactId?: string;
  errors?: Errors;
  readonly?: boolean;
  disabled?: boolean;
  hideSubtitles?: boolean;
}> = ({
  learnerNumber,
  posFields,
  readonly,
  contactId,
  disabled = false,
  hideSubtitles: hideSubSectionSubTitles = false,
}) => {
  const { t } = useTranslation();
  const hasPosFields = (section: "Contact" | "Event" | "Path") => {
    return (
      posFields &&
      posFields.filter(field => field && field.section === section).length > 0
    );
  };

  const hasContactFields = hasPosFields("Contact");
  const hasEventFields = hasPosFields("Event");
  const hasPathFields = hasPosFields("Path");
  if (!hasContactFields && !hasEventFields && !hasPosFields) {
    return null;
  }

  const getAdditionalProps = (type: CustomFieldType) => {
    if (browserLocaleTypes.includes(type)) {
      return { useBrowserLocale: true };
    }
    return undefined;
  };

  const isValid = (field: Maybe<PosFieldDef>) => (value: string | string[]) => {
    if (readonly) {
      return true;
    }

    if (!field?.isRequired) {
      return true;
    }

    return Array.isArray(value) || isObservableArray(value)
      ? !!value.length
      : !!value;
  };

  const renderField = (field: PosFieldDef) => {
    if (field.type === "province") {
      return (
        <CountryProvinceSelector
          contactId={contactId}
          name={`posFieldValues.${field.key}`}
          definition={field}
          required={!!field.isRequired}
          key={`learner-${learnerNumber}-${field.key}`}
        />
      );
    }

    return (
      <Col sm={6} key={`learner-${learnerNumber}-${field.key}`}>
        <CustomFieldInput
          name={`posFieldValues.${field.key}`}
          label={field.label}
          type={field.type as CustomFieldType}
          options={field.options || undefined}
          valid={isValid(field)}
          disabled={readonly || disabled}
          uniqueId={getUniqueId()}
          additionalInputProps={getAdditionalProps(
            field.type as CustomFieldType,
          )}
        />
      </Col>
    );
  };

  return (
    <Fragment>
      {posFields && hasContactFields && (
        <WrappedSubSection
          skipSubSection={hideSubSectionSubTitles}
          title={t("contactInformation")}
        >
          <Row>
            {posFields
              .filter(field => field.section === "Contact")
              .map(field => renderField(field))}
          </Row>
        </WrappedSubSection>
      )}
      {posFields && hasEventFields && (
        <WrappedSubSection
          skipSubSection={hideSubSectionSubTitles}
          title={t("eventInformation")}
        >
          <Row>
            {posFields
              .filter(field => field.section === "Event")
              .map(field => renderField(field))}
          </Row>
        </WrappedSubSection>
      )}
      {posFields && hasPathFields && (
        <WrappedSubSection
          skipSubSection={hideSubSectionSubTitles}
          title={t("pathInformation")}
        >
          <Row>
            {posFields
              .filter(field => field.section === "Path")
              .map(field => renderField(field))}
          </Row>
        </WrappedSubSection>
      )}
    </Fragment>
  );
};
